import {
  DexTradeTemplate,
  LimitOrder,
  OpenPositionListItem,
  StopLossEntry,
  TakeProfitEntry,
} from 'api/types/httpsTypes/dex-trade';
import {
  EntryKind,
  FormValues,
  LimitBuyAmountSchema,
  OrderKind,
  SnipeAmountSchema,
} from '../../schema';
import Big from 'big.js';
import {
  chainHasBribe,
  chainHasMemPool,
  chainMaxBuyGasPriority,
} from 'api/types/httpsTypes/d-wallets';
import { ContractInfo } from 'api/types/httpsTypes/contracts';
import { useQueryParams } from 'modules/routing/useQueryParams';
import * as yup from 'yup';
import { fromBase64 } from 'utils/encoding';
import { templateToDefaults } from '../templates/TemplatesBar';
import { fromAmount } from '../../../components/chains/units';

export function positionToTradeFormValues(
  trade: OpenPositionListItem
): FormValues {
  const tp0 = (trade.takeProfits?.[0] || trade.trailingStopLoss) as
    | TakeProfitEntry
    | StopLossEntry
    | undefined;

  const isSLFailed = trade.transactions
    .find((x) => x.id === trade.trailingStopLoss?.transactionId)
    ?.status?.includes('FAILED');

  const entryKind = !trade.isPostLaunchSnipe ? EntryKind.Snipe : EntryKind.Buy;

  const autoRetryEnabled = !!(
    trade.takeProfits?.[0]?.retry ||
    trade.trailingStopLoss?.retry ||
    trade.limitOrders?.[0]?.retry
  );

  const autoRetry = {
    buy: {
      active: !!trade.limitOrders?.[0]?.retry,
      nRetries: trade.limitOrders?.[0]?.nRetries ?? 0,
    },
    takeProfit: {
      active: !!trade.takeProfits?.[0]?.retry,
      nRetries: trade.takeProfits?.[0]?.nRetries ?? 0,
    },
    stopLoss: {
      active: !!trade.trailingStopLoss?.retry,
      nRetries: trade.trailingStopLoss?.nRetries ?? 0,
    },
  };

  function marketBuyOrderMapping() {
    return {
      amount: trade.buyAmountInWei
        ? fromAmount(trade.buyAmountInWei, trade.chain, 'weiToEther')
        : ('' as any),
      maxBuyGasPriority: fromAmount(
        trade.maxPriorityFeePerGasWei,
        trade.chain,
        'weiToGwei'
      ),
      bribe:
        chainHasBribe(trade.chain) && trade.antiMev
          ? fromAmount(trade.bribe, trade.chain, 'weiToEther')
          : 0,
      slippage: Big(trade.maxSlippage ?? 0)
        .times(100)
        .toNumber(),
      antiMev: trade.antiMev ?? false,
    };
  }

  function limitBuyOrderMapping(): LimitBuyAmountSchema {
    const firstLimitOrder = trade.limitOrders[0] as LimitOrder | undefined;

    return {
      orders: trade.limitOrders
        ? trade.limitOrders
            .map((order) => {
              const isFailed = trade.transactions
                .find((x) => x.id === order.id)
                ?.status?.includes('FAILED');
              return {
                id: order.id,
                status: order.status,
                isFailed,
                priceDeviationPercentage: Big(order.priceDeviationPercentage)
                  .neg()
                  .times(100)
                  .toNumber(),
                amount: fromAmount(order.amount, trade.chain, 'weiToEther'),
                price: Big(order.price ?? 0).toNumber(),
              };
            })
            .sort((a) => (a.status === 'ACTIVE' ? 1 : -1))
        : [],
      maxBuyGasPriority: firstLimitOrder
        ? fromAmount(
            firstLimitOrder.maxPriorityFeePerGasWei,
            trade.chain,
            'weiToGwei'
          )
        : Big(0).toNumber(),
      bribe:
        chainHasBribe(trade.chain) && trade.antiMev
          ? fromAmount(trade.bribe, trade.chain, 'weiToEther')
          : 0,
      antiMev: firstLimitOrder ? firstLimitOrder.antiMev ?? false : false,
      slippage: firstLimitOrder
        ? Big(firstLimitOrder.maxSlippage).times(100).toNumber()
        : Big(0).toNumber(),
      expirationInHours: firstLimitOrder
        ? Big(firstLimitOrder.expirationInHours).toNumber()
        : Big(0).toNumber(),
    };
  }

  function snipeOrderMapping(): SnipeAmountSchema {
    return {
      amount: trade.buyAmountInWei
        ? fromAmount(trade.buyAmountInWei ?? 0, trade.chain, 'weiToEther')
        : ('' as any),
      bribe: fromAmount(trade.bribe ?? 0, trade.chain, 'weiToEther'),
      blockOneRetry: trade.blockOneRetry || false,
      ...(trade.blockOneRetry
        ? {
            slippage: Big(trade.maxSlippage ?? 0)
              .times(100)
              .toNumber(),
            backupBribe: fromAmount(
              trade.backupBribe ?? 0,
              trade.chain,
              'weiToEther'
            ),
          }
        : {}),
    };
  }

  return {
    chain: trade.chain,
    address: trade.tokenAddress,
    wallets: [trade.vaultId],
    buyOrder:
      entryKind === EntryKind.Snipe
        ? {
            kind: chainHasMemPool(trade.chain)
              ? OrderKind.Snipe
              : OrderKind.SnipeWithoutMemPool,
            entries: chainHasMemPool(trade.chain)
              ? snipeOrderMapping()
              : marketBuyOrderMapping(),
          }
        : trade.limitOrders && trade.limitOrders.length > 0
        ? {
            kind: OrderKind.Limit,
            entries: limitBuyOrderMapping(),
          }
        : {
            kind: OrderKind.Market,
            entries: marketBuyOrderMapping(),
          },
    entryKind,
    safetyMeasures: {
      autoRetryEnabled,
      autoRetry,
      honeypotProtection: trade.honeypotProtection,
      gasLimitEnabled: !!trade.gasLimit,
      gasLimit: Big(trade.gasLimit || 0),
      marketCapRange:
        Big(trade.minMarketcapInUsd).gt(0) ||
        Big(trade.maxMarketcapInUsd).lt(Number.MAX_SAFE_INTEGER),
      taxProtectionEnabled:
        Big(trade.maxBuyTax).lt(1) || Big(trade.maxSellTax).lt(1),

      liquidityRange:
        Big(trade.minLiquidityInUsd).gt(0) ||
        Big(trade.maxLiquidityInUsd).lt(Number.MAX_SAFE_INTEGER),
      liquidity: {
        min: +trade.minLiquidityInUsd,
        max: +trade.maxLiquidityInUsd,
      },
      marketCap: {
        min: +trade.minMarketcapInUsd,
        max: +trade.maxMarketcapInUsd,
      },
      taxProtection: {
        buy: Big(trade.maxBuyTax).times(100).toNumber(),
        sell: Big(trade.maxSellTax).times(100).toNumber(),
      },
    },
    takeProfit: trade.takeProfits
      .map((tp) => {
        const isFailed = trade.transactions
          .find((x) => x.id === tp.id)
          ?.status?.includes('FAILED');
        return {
          id: tp.id,
          status: tp.status,
          isFailed,
          threshold: Big(tp.priceIncreasePercentage).times(100).toNumber(),
          weight: Big(tp.amountToSellPercentage).times(100).toNumber(),
        };
      })
      .sort((a) => (a.status === 'ACTIVE' ? 1 : -1)),
    stopLoss: trade.trailingStopLoss
      ? {
          id: isSLFailed ? undefined : trade.trailingStopLoss.id,
          dynamic: trade.trailingStopLoss.dynamic,
          isTrailingEnabled:
            +trade.trailingStopLoss.trailingDeviationPercentage !== 1,
          deviation:
            +trade.trailingStopLoss.trailingDeviationPercentage === 1
              ? null
              : Big(trade.trailingStopLoss.trailingDeviationPercentage)
                  .times(100)
                  .toNumber(),
          threshold: Big(trade.trailingStopLoss.initialStopLossPercentage)
            .abs()
            .times(100)
            .toNumber(),
          isFailed: isSLFailed,
        }
      : null,
    takeProfitConfig: {
      maxSellGasPriority: tp0
        ? fromAmount(tp0?.maxPriorityFeePerGasWei, trade.chain, 'weiToGwei')
        : chainMaxBuyGasPriority(trade.chain),
      bribe:
        tp0 && chainHasBribe(trade.chain) && trade.antiMev
          ? fromAmount(tp0.bribe, trade.chain, 'weiToEther')
          : 0,
      slippage: tp0 ? Big(tp0?.maxSlippage).times(100).toNumber() : 15,
      antiMev: tp0 ? tp0.antiMev ?? false : false,
    },
  };
}

export function positionToStatelessFormValues(position: OpenPositionListItem) {
  return positionToTradeFormValues({
    ...position,
    takeProfits: position.takeProfits.map(
      ({
        priceIncreasePercentage,
        amountToSellPercentage,
        maxPriorityFeePerGasWei,
        maxSlippage,
      }) =>
        ({
          priceIncreasePercentage,
          amountToSellPercentage,
          maxPriorityFeePerGasWei,
          maxSlippage,
        } as TakeProfitEntry)
    ),
    trailingStopLoss: position.trailingStopLoss
      ? ({
          amountToSellPercentage:
            position.trailingStopLoss.amountToSellPercentage,
          maxPriorityFeePerGasWei:
            position.trailingStopLoss.maxPriorityFeePerGasWei,
          initialStopLossPercentage:
            position.trailingStopLoss.initialStopLossPercentage,
          maxSlippage: position.trailingStopLoss.maxSlippage,
          trailingDeviationPercentage:
            position.trailingStopLoss.trailingDeviationPercentage,
        } as StopLossEntry)
      : null,
    limitOrders: position.limitOrders.map(
      (limitOrder) =>
        ({
          priceDeviationPercentage: limitOrder.priceDeviationPercentage,
          amount: limitOrder.amount,
          maxPriorityFeePerGasWei: limitOrder.maxPriorityFeePerGasWei,
          maxSlippage: limitOrder.maxSlippage,
          expirationInHours: limitOrder.expirationInHours,
          side: limitOrder.side,
          comparisonOperator: limitOrder.comparisonOperator,
        } as LimitOrder)
    ),
  });
}

export function useDefaults(
  contract: ContractInfo,
  template: DexTradeTemplate
): Partial<FormValues> {
  const [{ defaultValues }] = useQueryParams({
    defaultValues: yup.string(),
  });

  if (defaultValues) {
    return JSON.parse(fromBase64(defaultValues)) as FormValues;
  }
  const entryKind = contract.tradingInfo.isSnipeable
    ? EntryKind.Snipe
    : EntryKind.Buy;

  return {
    ...templateToDefaults(entryKind, template, contract.generalInfo.chain.key),
    currentTokenPrice:
      contract.liquidityInfo.activePools[0]?.priceDollar || undefined,
    entryKind,
    address: contract.generalInfo.address,
    chain: contract.generalInfo.chain.key,
  };
}
